import React from 'react';
import {Box, Chip, IconButton, ListItem, Tooltip, Typography} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useSourcePackageBuilderStore from '../../../../stores/useSourcePackageBuilderStore';
import SourceScore from '../../../SourceScore';
import {listClaimCoverage} from './utils/getClaimCoverage';

/**
 * Suggested reference list item component with rich metadata display
 * Shows relevance score, matched tags, source score, and classification level
 * @param {Object}   props
 * @param {Object}   props.citation  - The suggested citation object with full metadata
 * @param {boolean}  props.isAdded   - Whether this reference has already been added
 * @param {Function} props.onAdd     - Callback to add this suggestion
 * @param {Function} props.onRemove  - Callback to remove this suggestion
 * @returns {JSX.Element}
 */
export default function SuggestedReferenceItem({citation, isAdded = false, onAdd, onRemove}) {
    const openSourcePackageBuilder = useSourcePackageBuilderStore(s => s.open);

    const handleOpenDocument = (e) => {
        // Don't trigger if clicking any button
        if (e.target.closest('button')) return;

        try {
            openSourcePackageBuilder({
                documentId: citation.docId,
                segmentIndex: citation.index
            });
        } catch (error) {
            console.error('Failed to open document:', error);
        }
    };

    const handleAdd = (e) => {
        e.stopPropagation();
        onAdd(citation);
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        onRemove(citation);
    };

    // Extract match info
    const matchInfo = citation.matchInfo || {};
    const proofLevel = citation.proofLevel != null ? Number(citation.proofLevel) : null;
    const relevanceScore = proofLevel != null
        ? proofLevel
        : (matchInfo.relevanceScore ?? null);
    const matchReason = citation.proofReason || matchInfo.matchReason || '';
    const matchedQueryTags = matchInfo.matchedQueryTags || [];
    const matchedPredominantTags = matchInfo.matchedPredominantTags || [];
    const claimCoverageRows = listClaimCoverage(citation);

    const isHighRelevance = relevanceScore != null && relevanceScore >= 0.7;

    // Get content to display (prefer content, fallback to summary)
    const displayContent = citation.content || citation.summary?.text || '';

    // Classification level badge
    const classificationLevel = citation.docClassificationLevel;

    // Source type
    const sourceType = citation.docSourceType;

    // Source type chip colours
    const sourceTypeStyle = (() => {
        switch (sourceType) {
            case 'Corpus':
                return {bgcolor: 'rgba(25, 118, 210, 0.1)', color: '#1565c0'};
            case 'My Data':
                return {bgcolor: 'rgba(123, 31, 162, 0.1)', color: '#6a1b9a'};
            default:
                return {bgcolor: 'rgba(0,0,0,0.07)', color: 'text.secondary'};
        }
    })();

    return (
        <ListItem
            onClick={handleOpenDocument}
            sx={{
                p: 2,
                pr: 1,
                display: 'flex',
                gap: 1,
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                bgcolor: isAdded
                    ? 'rgba(46, 125, 50, 0.08)'
                    : isHighRelevance ? 'rgba(46, 125, 50, 0.04)' : 'transparent',
                '&:hover': {
                    bgcolor: isAdded
                        ? 'rgba(46, 125, 50, 0.13)'
                        : isHighRelevance ? 'rgba(46, 125, 50, 0.08)' : 'rgba(0,0,0,0.06)',
                },
            }}
        >
            {/* Left side: Content */}
            <Box sx={{flex: 1, minWidth: 0}}>
                {/* Title row: source-type chip + full title + classification badge */}
                <Box sx={{display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.5, mb: 0.25}}>
                    {sourceType && (
                        <Chip
                            label={sourceType}
                            size="small"
                            sx={{
                                height: 16,
                                fontSize: '0.6rem',
                                fontWeight: 700,
                                letterSpacing: '0.03em',
                                flexShrink: 0,
                                ...sourceTypeStyle,
                            }}
                        />
                    )}
                    <Typography
                        variant="subtitle2"
                        component="span"
                        sx={{
                            fontWeight: 600,
                            lineHeight: 1.4,
                            wordBreak: 'break-word',
                            flex: '1 1 auto',
                            minWidth: 0,
                        }}
                    >
                        {citation.docTitle || 'Suggested Reference'}
                    </Typography>
                    {classificationLevel && (
                        <Chip
                            label={classificationLevel}
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                fontWeight: 600,
                                flexShrink: 0,
                                bgcolor: 'rgba(0, 0, 0, 0.08)',
                            }}
                        />
                    )}
                </Box>

                {/* Source row — just docSource, type already shown as chip */}
                {citation.docSource && (
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'text.secondary',
                            display: 'block',
                            wordBreak: 'break-word',
                            mb: 0.5,
                        }}
                    >
                        {citation.docSource}
                    </Typography>
                )}

                {/* Content preview */}
                {displayContent && (
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 0.5,
                            mb: 0.75,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.4,
                        }}
                    >
                        {displayContent}
                    </Typography>
                )}

                {/* Metadata chips row */}
                <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.75}}>
                    {/* Relevance score chip */}
                    {relevanceScore != null && (
                        <Tooltip
                            title={matchReason || (proofLevel != null ? 'Proof level from evidence filter' : 'Relevance score based on content match')}
                            arrow
                        >
                            <Chip
                                label={proofLevel != null
                                    ? `Proof: ${(relevanceScore * 100).toFixed(0)}%`
                                    : `Relevance: ${(relevanceScore * 100).toFixed(0)}%`}
                                size="small"
                                sx={{
                                    height: 20,
                                    fontSize: '0.7rem',
                                    bgcolor: isHighRelevance ? 'rgba(46, 125, 50, 0.12)' : 'rgba(0, 0, 0, 0.08)',
                                    color: isHighRelevance ? '#2e7d32' : 'text.secondary',
                                    fontWeight: isHighRelevance ? 600 : 400,
                                }}
                            />
                        </Tooltip>
                    )}

                    {claimCoverageRows.map((row) => (
                        <Chip
                            key={`cov-${row.claimId}`}
                            label={`Claim ${row.claimId}: ${(row.coverage * 100).toFixed(0)}%`}
                            size="small"
                            variant="outlined"
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                borderColor: 'primary.light',
                                color: 'primary.main',
                            }}
                        />
                    ))}

                    {/* Matched query tags */}
                    {matchedQueryTags.slice(0, 3).map((tag, idx) => (
                        <Chip
                            key={idx}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                borderColor: 'primary.main',
                                color: 'primary.main',
                            }}
                        />
                    ))}

                    {/* Matched predominant tags */}
                    {matchedPredominantTags.slice(0, 2).map((tag, idx) => (
                        <Chip
                            key={`pred-${idx}`}
                            label={tag}
                            size="small"
                            variant="outlined"
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                borderColor: 'rgba(0, 0, 0, 0.23)',
                                color: 'text.secondary',
                            }}
                        />
                    ))}

                    {/* Show "+N more" if there are additional tags */}
                    {(matchedQueryTags.length + matchedPredominantTags.length > 5) && (
                        <Chip
                            label={`+${matchedQueryTags.length + matchedPredominantTags.length - 5} more`}
                            size="small"
                            sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                bgcolor: 'rgba(0, 0, 0, 0.04)',
                                color: 'text.secondary',
                            }}
                        />
                    )}
                </Box>
            </Box>

            {/* Right side: Source Score + Actions */}
            <Box sx={{display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5}}>
                {/* Source Score */}
                {citation.sourceScore != null && (
                    <Box sx={{mb: 0.5}}>
                        <SourceScore
                            value={citation.sourceScore}
                            onChange={() => {
                            }} // Read-only for suggestions
                            sx={{pointerEvents: 'none'}} // Disable interaction
                        />
                    </Box>
                )}

                {/* Action buttons */}
                <Box sx={{display: 'flex', gap: 0.5}}>
                    <IconButton
                        size="small"
                        onClick={handleAdd}
                        disabled={isAdded}
                        sx={{
                            width: 28,
                            height: 28,
                            padding: 0,
                            color: isAdded ? 'success.main' : 'success.main',
                            bgcolor: isAdded ? 'rgba(46, 125, 50, 0.12)' : 'transparent',
                            '&:hover': {
                                bgcolor: isAdded ? 'rgba(46, 125, 50, 0.12)' : 'rgba(46, 125, 50, 0.08)',
                            },
                            '&.Mui-disabled': {
                                color: 'success.main',
                            },
                        }}
                        title={isAdded ? 'Already added' : 'Add to references'}
                    >
                        {isAdded ? <CheckIcon fontSize="small"/> : <AddIcon fontSize="small"/>}
                    </IconButton>
                    {!isAdded && (
                        <IconButton
                            size="small"
                            onClick={handleRemove}
                            sx={{
                                width: 28,
                                height: 28,
                                padding: 0,
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                            title="Remove suggestion"
                        >
                            <DeleteOutlineIcon fontSize="small"/>
                        </IconButton>
                    )}
                </Box>
            </Box>
        </ListItem>
    );
}
